import React, { useEffect, useState, useRef } from "react";
import CreateCategory from "./CreateCategory";
import AddSongs from "./AddSongs";
import {
  PlusCircle,
  Music,
  LayoutDashboard,
  ChevronDown,
  ChevronUp,
  Play,
  Pause,
  Trash2,
  Search,
  X,
} from "lucide-react";
import { useDispatch, useSelector } from "react-redux";
import {
  getAllCategories,
  deleteSong,
  deleteCategory,
} from "../../redux/reduxSlices/manageCategorySlice";
import toast from "react-hot-toast";
import Header from "../common/Header";

const CategoryManagement = ({ user, onLogout }) => {
  const dispatch = useDispatch();
  const [activeTab, setActiveTab] = useState("dashboard");
  const [expandedCategories, setExpandedCategories] = useState({});
  const [searchTerm, setSearchTerm] = useState("");
  const [currentlyPlaying, setCurrentlyPlaying] = useState({
    categoryId: null,
    songUrl: null,
    audio: null,
  });

  // Popup state
  const [showPopup, setShowPopup] = useState(false);
  const [popupConfig, setPopupConfig] = useState({
    title: "",
    message: "",
    onConfirm: null,
    type: "danger", // danger or warning
  });

  const { loading, success, getAllCategoriesResponse } = useSelector(
    (state) => state.manageCategory,
  );

  useEffect(() => {
    dispatch(getAllCategories());
  }, []);

  // Function to stop all playing songs
  const stopAllSongs = () => {
    if (currentlyPlaying.audio) {
      currentlyPlaying.audio.pause();
      setCurrentlyPlaying({
        categoryId: null,
        songUrl: null,
        audio: null,
      });
    }
  };

  // Handle tab change
  const handleTabChange = (tabId) => {
    stopAllSongs();
    setActiveTab(tabId);
  };

  // Filter categories based on search term
  const filteredCategories =
    getAllCategoriesResponse?.data?.filter((category) =>
      category.name.toLowerCase().includes(searchTerm.toLowerCase()),
    ) || [];

  const toggleCategory = (categoryId) => {
    setExpandedCategories((prev) => ({
      ...prev,
      [categoryId]: !prev[categoryId],
    }));
  };

  const handlePlayPause = (categoryId, songUrl, songTitle) => {
    if (
      currentlyPlaying.audio &&
      currentlyPlaying.categoryId === categoryId &&
      currentlyPlaying.songUrl === songUrl
    ) {
      currentlyPlaying.audio.pause();
      setCurrentlyPlaying({
        categoryId: null,
        songUrl: null,
        audio: null,
      });
    } else {
      if (currentlyPlaying.audio) {
        currentlyPlaying.audio.pause();
      }

      const audio = new Audio(songUrl);
      audio.play();

      audio.addEventListener("ended", () => {
        setCurrentlyPlaying({
          categoryId: null,
          songUrl: null,
          audio: null,
        });
      });

      setCurrentlyPlaying({
        categoryId,
        songUrl,
        audio,
      });
    }
  };

  // Show delete song popup
  const showDeleteSongPopup = (categoryId, subCategory, songId, songTitle) => {
    setPopupConfig({
      title: "Delete Song",
      message: `Are you sure you want to delete the song "${songTitle}"?`,
      onConfirm: () => handleDeleteSong(categoryId, subCategory, songId),
      type: "danger",
    });
    setShowPopup(true);
  };

  // Show delete category popup
  const showDeleteCategoryPopup = (categoryId, categoryName) => {
    setPopupConfig({
      title: "Delete Category",
      message: `Are you sure you want to delete the category "${categoryName}"? This will delete all songs in this category.`,
      onConfirm: () => handleDeleteCategory(categoryId, categoryName),
      type: "danger",
    });
    setShowPopup(true);
  };

  const handleDeleteSong = async (categoryId, subCategory, songId) => {
    setShowPopup(false);
    try {
      const result = await dispatch(
        deleteSong({ categoryId, subCategory, songId }),
      ).unwrap();
      if (result.success) {
        toast.success(result.message || "Song deleted successfully");
        dispatch(getAllCategories());
      } else {
        toast.error(result.message || "Failed to delete song");
      }
    } catch (error) {
      toast.error(error.message || "An error occurred while deleting the song");
    }
  };

  const handleDeleteCategory = async (categoryId, categoryName) => {
    setShowPopup(false);
    try {
      const result = await dispatch(deleteCategory(categoryId)).unwrap();
      if (result.success) {
        toast.success(result.message || "Category deleted successfully");
        await dispatch(getAllCategories());
        setExpandedCategories((prev) => {
          const newState = { ...prev };
          delete newState[categoryId];
          return newState;
        });
      } else {
        toast.error(result.message || "Failed to delete category");
      }
    } catch (error) {
      toast.error(
        error.message || "An error occurred while deleting the category",
      );
    }
  };

  const closePopup = () => {
    setShowPopup(false);
  };

  // Cleanup audio on component unmount
  useEffect(() => {
    return () => {
      if (currentlyPlaying.audio) {
        currentlyPlaying.audio.pause();
      }
    };
  }, []);

  const tabs = [
    { id: "create", label: "Create Category", icon: PlusCircle },
    { id: "add", label: "Add Songs", icon: Music },
    { id: "dashboard", label: "Dashboard", icon: LayoutDashboard },
  ];

  return (
    <div className="min-h-screen bg-gradient-to-br from-orange-50 via-white to-amber-50">
      <Header user={user} onLogout={onLogout} />
      {/* Simple Popup Modal */}
      {showPopup && (
        <div className="fixed inset-0 z-50 flex items-center justify-center bg-black bg-opacity-50">
          <div className="bg-white rounded-lg p-6 max-w-md w-full mx-4">
            <div className="flex justify-between items-center mb-4">
              <h3 className="text-lg font-semibold text-gray-800">
                {popupConfig.title}
              </h3>
              <button
                onClick={closePopup}
                className="text-gray-400 hover:text-gray-600"
              >
                <X size={20} />
              </button>
            </div>

            <p className="text-gray-600 mb-6">{popupConfig.message}</p>

            <div className="flex gap-3 justify-end">
              <button
                onClick={closePopup}
                className="px-4 py-2 text-gray-600 bg-gray-100 rounded-lg hover:bg-gray-200 transition-colors"
              >
                Cancel
              </button>
              <button
                onClick={popupConfig.onConfirm}
                className="px-4 py-2 text-white bg-red-500 rounded-lg hover:bg-red-600 transition-colors"
              >
                Delete
              </button>
            </div>
          </div>
        </div>
      )}

      <div className="container mx-auto px-4 py-8">
        {/* Header */}
        <div className="text-center mb-8">
          <h1 className="text-4xl font-bold bg-gradient-to-r from-orange-600 to-amber-600 bg-clip-text text-transparent pt-4 pb-1">
            Category Management
          </h1>
          <p className="text-gray-600 mt-2">
            Manage your jingle categories and songs
          </p>
        </div>

        {/* Tabs */}
        <div className="max-w-6xl mx-auto">
          <div className="border-b border-gray-200 mb-8">
            <nav className="flex space-x-8" aria-label="Tabs">
              {tabs.map((tab) => {
                const Icon = tab.icon;
                return (
                  <button
                    key={tab.id}
                    onClick={() => handleTabChange(tab.id)}
                    className={`
                      flex items-center gap-2 py-4 px-1 border-b-2 font-medium text-sm transition-all duration-300
                      ${
                        activeTab === tab.id
                          ? "border-orange-500 text-orange-600"
                          : "border-transparent text-gray-500 hover:text-gray-700 hover:border-gray-300"
                      }
                    `}
                  >
                    <Icon size={18} />
                    {tab.label}
                  </button>
                );
              })}
            </nav>
          </div>

          {/* Tab Content */}
          <div className="bg-white rounded-2xl shadow-xl p-6">
            {activeTab === "create" && <CreateCategory />}
            {activeTab === "add" && <AddSongs />}
            {activeTab === "dashboard" && (
              <div className="space-y-6">
                {/* Search Bar */}
                <div className="relative">
                  <Search
                    className="absolute left-3 top-1/2 transform -translate-y-1/2 text-gray-400"
                    size={20}
                  />
                  <input
                    type="text"
                    placeholder="Search categories..."
                    value={searchTerm}
                    onChange={(e) => setSearchTerm(e.target.value)}
                    className="w-full pl-10 pr-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-orange-500 focus:border-orange-500"
                  />
                </div>

                {/* Categories List */}
                {loading ? (
                  <div className="text-center py-12">
                    <div className="inline-block animate-spin rounded-full h-8 w-8 border-b-2 border-orange-500"></div>
                    <p className="mt-2 text-gray-500">Loading categories...</p>
                  </div>
                ) : filteredCategories.length === 0 ? (
                  <div className="text-center py-12">
                    <Music className="mx-auto h-16 w-16 text-gray-400 mb-4" />
                    <h3 className="text-xl font-semibold text-gray-700 mb-2">
                      {searchTerm
                        ? "No categories found"
                        : "No categories available"}
                    </h3>
                    <p className="text-gray-500">
                      {searchTerm
                        ? `No categories matching "${searchTerm}"`
                        : "Create your first category to get started"}
                    </p>
                  </div>
                ) : (
                  <div className="space-y-4">
                    {filteredCategories.map((category) => (
                      <div
                        key={category.id}
                        className="border border-gray-200 rounded-lg overflow-hidden"
                      >
                        {/* Category Header */}
                        <div
                          className="flex items-center justify-between p-4 bg-gradient-to-r from-orange-50 to-amber-50 cursor-pointer hover:from-orange-100 hover:to-amber-100 transition-all duration-300"
                          onClick={() => toggleCategory(category.id)}
                        >
                          <div className="flex items-center gap-4">
                            {category.image && (
                              <img
                                src={category.image}
                                alt={category.name}
                                className="w-12 h-12 rounded-lg object-cover"
                              />
                            )}
                            <div>
                              <h3 className="text-lg font-semibold text-gray-800 capitalize">
                                {category.name}
                              </h3>
                              <p className="text-sm text-gray-500">
                                {category.songs?.length}{" "}
                                {category.songs?.length === 1
                                  ? "song"
                                  : "songs"}{" "}
                                • {category.songs?.length || 0} languages
                              </p>
                            </div>
                          </div>
                          <div className="flex items-center gap-2">
                            <button
                              onClick={(e) => {
                                e.stopPropagation();
                                showDeleteCategoryPopup(
                                  category.id,
                                  category.name,
                                );
                              }}
                              className="p-2 text-red-500 hover:bg-red-50 rounded-lg transition-colors"
                            >
                              <Trash2 size={18} />
                            </button>
                            {expandedCategories[category.id] ? (
                              <ChevronUp size={20} className="text-gray-500" />
                            ) : (
                              <ChevronDown
                                size={20}
                                className="text-gray-500"
                              />
                            )}
                          </div>
                        </div>

                        {/* Category Content (Accordion) */}
                        {expandedCategories[category.id] && (
                          <div className="p-4 space-y-6">
                            {category.songs && category.songs.length > 0 ? (
                              category.songs.map((subCategory, idx) => (
                                <div key={idx} className="space-y-3">
                                  {/* SubCategory Header */}
                                  <h4 className="text-md font-semibold text-orange-600 border-l-4 border-orange-500 pl-3">
                                    {subCategory.subCategory}
                                  </h4>

                                  {/* Songs List */}
                                  <div className="space-y-2 ml-4">
                                    {subCategory.songList.map(
                                      (song, songIdx) => (
                                        <div
                                          key={song.id || songIdx}
                                          className="flex items-center justify-between p-3 bg-gray-50 rounded-lg hover:bg-gray-100 transition-colors"
                                        >
                                          <div className="flex-1">
                                            <p className="font-medium text-gray-800">
                                              {song.title}
                                            </p>
                                            <p className="text-sm text-gray-500">
                                              Genre: {song.genre}
                                            </p>
                                          </div>
                                          <div className="flex items-center gap-2">
                                            <button
                                              onClick={() =>
                                                handlePlayPause(
                                                  category.id,
                                                  song.url,
                                                  song.title,
                                                )
                                              }
                                              className={`p-2 rounded-full transition-colors ${
                                                currentlyPlaying.categoryId ===
                                                  category.id &&
                                                currentlyPlaying.songUrl ===
                                                  song.url
                                                  ? "bg-orange-500 text-white hover:bg-orange-600"
                                                  : "bg-gray-200 text-gray-700 hover:bg-orange-100"
                                              }`}
                                            >
                                              {currentlyPlaying.categoryId ===
                                                category.id &&
                                              currentlyPlaying.songUrl ===
                                                song.url ? (
                                                <Pause size={16} />
                                              ) : (
                                                <Play size={16} />
                                              )}
                                            </button>
                                            <button
                                              onClick={() =>
                                                showDeleteSongPopup(
                                                  category.id,
                                                  subCategory.subCategory,
                                                  song.id,
                                                  song.title,
                                                )
                                              }
                                              className="p-2 text-red-500 hover:bg-red-50 rounded-full transition-colors"
                                            >
                                              <Trash2 size={16} />
                                            </button>
                                          </div>
                                        </div>
                                      ),
                                    )}
                                  </div>
                                </div>
                              ))
                            ) : (
                              <div className="text-center py-8 text-gray-500">
                                No songs available in this category
                              </div>
                            )}
                          </div>
                        )}
                      </div>
                    ))}
                  </div>
                )}
              </div>
            )}
          </div>
        </div>
      </div>
    </div>
  );
};

export default CategoryManagement;
